"use client";

import { AuthPageHeading } from "@/app/(front-door)/_components/AuthPageHeading";
import { FrontDoorNotice } from "@/app/(front-door)/_components/FrontDoorNotice";
import {
  FRONT_DOOR_CARD_CLASS,
  FRONT_DOOR_FIELD_STYLES,
  FRONT_DOOR_HELP_CLASS,
  FRONT_DOOR_LINK_CLASS,
  FRONT_DOOR_PRIMARY_BUTTON_CLASS,
} from "@/app/(front-door)/_components/styles";
import { Button } from "@/app/_components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/_components/ui/form";
import { Input } from "@/app/_components/ui/input";
import { MarbleLink as Link } from "@/app/_components/ui/marble-link";
import {
  EmailSchema,
  NewPasswordSchema,
  TAuthSchema,
  TEmailSchema,
  TNewPasswordSchema,
} from "@/lib/modules/auth/schemas";
import type { TokenError } from "@/lib/modules/auth/types";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, LoaderCircleIcon, Mail } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

interface PasswordResetFormProps {
  sendPasswordResetRequestAction: (email: string) => Promise<void>;
  getTokenValidAction: (
    token: string,
  ) => Promise<{ tokenError?: TokenError; email?: string }>;
  sendPasswordResetAction: (
    credentials: TAuthSchema,
    token: string,
  ) => Promise<{ tokenError?: TokenError }>;
}

export default function PasswordResetForm({
  sendPasswordResetRequestAction,
  sendPasswordResetAction,
  getTokenValidAction,
}: PasswordResetFormProps) {
  const searchParams = useSearchParams();
  const token = searchParams?.get("token");

  const [submittedEmail, setSubmittedEmail] = useState<string>();
  const [loadingTokenCheck, setLoadingTokenCheck] = useState(!!token);
  const [tokenError, setTokenError] = useState<TokenError | null>(null);
  const [tokenEmail, setTokenEmail] = useState<string>();
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(false);

  const emailForm = useForm<TEmailSchema>({
    resolver: zodResolver(EmailSchema),
    defaultValues: {
      email: "",
    },
  });

  const newPasswordForm = useForm<TNewPasswordSchema>({
    resolver: zodResolver(NewPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: TEmailSchema) => {
    try {
      setLoading(true);
      await sendPasswordResetRequestAction(values.email);
      setSubmittedEmail(values.email);
    } catch {
      toast("🔴 Error requesting a password reset", {
        duration: 5000,
        className: "w-fit right-0",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    async function updateTokenCheck() {
      if (token) {
        const { tokenError, email } = await getTokenValidAction(token);
        if (tokenError) setTokenError(tokenError);
        if (email) setTokenEmail(email);
        setLoadingTokenCheck(false);
      }
    }
    updateTokenCheck();
  }, [getTokenValidAction, token]);

  const onSubmitNewPassword = async (values: TNewPasswordSchema) => {
    if (!token || !tokenEmail) return;
    setLoading(true);
    try {
      const result = await sendPasswordResetAction(
        { email: tokenEmail, password: values.password },
        token,
      );
      if (result.tokenError) {
        setTokenError(result.tokenError);
        setTokenEmail(undefined);
      } else {
        setComplete(true);
      }
    } catch {
      toast("An error occurred resetting your password. Please try again.", {
        duration: 5000,
        className: "w-fit right-0",
      });
    } finally {
      setLoading(false);
    }
  };

  const state = _getResetState({
    isComplete: complete,
    isCheckingToken: loadingTokenCheck,
    hasTokenError: !!tokenError,
    tokenEmail,
    submittedEmail,
  });
  const heading = RESET_HEADINGS[state];

  return (
    <div>
      <AuthPageHeading title={heading.title} lead={heading.lead} />
      <div className={FRONT_DOOR_CARD_CLASS}>
        {state === "request" && (
          <Form {...emailForm}>
            <form
              onSubmit={emailForm.handleSubmit(onSubmit)}
              method="post"
              className={FRONT_DOOR_FIELD_STYLES}
            >
              <FormField
                control={emailForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        autoComplete="email"
                        placeholder="you@email.com"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                variant="custom"
                className={`mt-[22px] ${FRONT_DOOR_PRIMARY_BUTTON_CLASS}`}
                loading={loading}
              >
                Submit
              </Button>
            </form>
          </Form>
        )}

        {state === "sent" && (
          <div className="text-center">
            <span className="mx-auto mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-full bg-primary-subtle">
              <Mail className="h-6 w-6 text-primary-strong" aria-hidden />
            </span>
            <p className="text-[15.5px] leading-[1.55] text-foreground-soft">
              {"Please check your email at "}
              <strong className="break-all text-foreground">
                {submittedEmail}
              </strong>
              {" for instructions to securely reset your password."}
            </p>
          </div>
        )}

        {state === "checking" && (
          <div className="flex flex-col items-center gap-3 py-3.5">
            <LoaderCircleIcon
              className="h-7 w-7 animate-spin text-primary"
              aria-hidden
            />
            <span className={FRONT_DOOR_HELP_CLASS}>Checking your link…</span>
          </div>
        )}

        {state === "new-password" && (
          <Form {...newPasswordForm}>
            <form
              onSubmit={newPasswordForm.handleSubmit(onSubmitNewPassword)}
              method="post"
              className={FRONT_DOOR_FIELD_STYLES}
            >
              <label
                htmlFor="reset-email"
                className="mb-2 block text-[15px] font-semibold text-foreground"
              >
                Email
              </label>
              <Input
                id="reset-email"
                type="email"
                disabled
                value={tokenEmail}
              />
              <FormField
                control={newPasswordForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="mt-[18px]">
                    <FormLabel>New password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        autoComplete="new-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={newPasswordForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="mt-[18px]">
                    <FormLabel>Confirm new password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        autoComplete="new-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                variant="custom"
                className={`mt-[22px] ${FRONT_DOOR_PRIMARY_BUTTON_CLASS}`}
                loading={loading}
              >
                Update password
              </Button>
            </form>
          </Form>
        )}

        {state === "invalid" && (
          <FrontDoorNotice tone="error">
            <strong>This link is invalid or has expired.</strong>
            {" Return to sign in and request a new one."}
          </FrontDoorNotice>
        )}

        {state === "done" ? (
          <div className="text-center">
            <span className="mx-auto mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-full bg-success-subtle">
              <Check
                className="h-6 w-6 text-success"
                strokeWidth={2.5}
                aria-hidden
              />
            </span>
            <p className="mb-5 text-[15.5px] leading-[1.55] text-foreground-soft">
              Please return to the sign in page to sign in with your new
              password.
            </p>
            <Link
              href="/auth/login"
              className={cn(
                "inline-flex items-center justify-center",
                FRONT_DOOR_PRIMARY_BUTTON_CLASS,
              )}
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <div className="mt-5 text-center">
            <Link
              href="/auth/login"
              className={`text-[14.5px] ${FRONT_DOOR_LINK_CLASS}`}
            >
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

type ResetState =
  | "request"
  | "sent"
  | "checking"
  | "new-password"
  | "invalid"
  | "done";

const RESET_HEADINGS: Record<ResetState, { title: string; lead?: string }> = {
  request: {
    title: "Forgot password",
    lead: "Enter your email address, and we'll send you instructions to securely reset your password.",
  },
  sent: { title: "Check your email" },
  checking: { title: "Reset your password" },
  "new-password": {
    title: "Reset your password",
    lead: "Please enter your new password.",
  },
  invalid: { title: "Reset your password" },
  done: { title: "Your password has been reset!" },
};

const _getResetState = ({
  isComplete,
  isCheckingToken,
  hasTokenError,
  tokenEmail,
  submittedEmail,
}: {
  isComplete: boolean;
  isCheckingToken: boolean;
  hasTokenError: boolean;
  tokenEmail?: string;
  submittedEmail?: string;
}): ResetState => {
  if (isComplete) {
    return "done";
  }
  if (isCheckingToken) {
    return "checking";
  }
  if (hasTokenError) {
    return "invalid";
  }
  if (tokenEmail) {
    return "new-password";
  }
  return submittedEmail ? "sent" : "request";
};
